import {useContext, useState} from 'react';

import {CircularProgress} from '@mui/material';
import {DeleteForever, FileUpload} from '@mui/icons-material';

import ImageCropModal from '../../../../../components/image-crop-modal/ImageCropModal';
import {request, URLs} from '../../../../../api';
import AppContext from '../../../../../context/AppContext';
import ConfirmationModal from '../../../../../components/modal/ConfirmationModal';

type ManageModulePictureProps = {
  currentImageUUID: string | null;
};

function ManageModulePicture({currentImageUUID}: ManageModulePictureProps) {
  const {user, dispatchError} = useContext(AppContext);

  const [image, setImage] = useState<string | null>(currentImageUUID ? URLs.moduleImageBucket(currentImageUUID) : null);
  const [fileUploading, setFileUploading] = useState(false);
  const [croppingImage, setCroppingImage] = useState<string | false>(false);

  const [isImageHovered, setIsImageHovered] = useState(false);
  const [isDeleteImageModalOpen, setIsDeleteImageModalOpen] = useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      setCroppingImage(URL.createObjectURL(file));
    }
  };

  const onFinishedCropping = async (croppedImage: string) => {
    if (user?.activeModule?.moduleId) {
      setCroppingImage(false);
      setFileUploading(true);

      const formData = new FormData();
      const blob = await (await fetch(croppedImage)).blob();
      formData.append('file', blob);

      request<{}, FormData, {uploadedImageName: string}>(URLs.moduleImage(user.activeModule.moduleId), {
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        body: formData,
        successCallback(response) {
          setImage(URLs.moduleImageBucket(response.uploadedImageName));
          setFileUploading(false);
        },
        errorCallback() {
          dispatchError({
            errorMessage: 'There was an error. Please try again later.',
          });
          setFileUploading(false);
          setImage(null);
        },
      });
    } else {
      dispatchError({
        errorMessage: 'There was an error. Make sure you selected a module.',
      });
    }
  };

  const handleDeleteImage = () => {
    if (user?.activeModule?.moduleId) {
      setImage(null);
      setIsDeleteImageModalOpen(false);
      setFileUploading(true);
      request<{}, {}, {}>(URLs.moduleImage(user.activeModule.moduleId), {
        method: 'DELETE',

        successCallback() {
          setFileUploading(false);
          setImage(null);
        },
        errorCallback() {
          dispatchError({
            errorMessage: 'There was an error. Please try again later.',
          });
          setFileUploading(false);
          setImage(null);
        },
      });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h3 className="font-bold text-2xl mb-0">Module Picture</h3>
        <div className="grid grid-cols-4 gap-3">
          <div className="col-span-4">
            <p className="mb-3 text-sm text-gray-400">
              Upload a new module picture. Changes will be applied immediately upon upload.
            </p>
            <p>
              The module picture provides a visual representation of the module. It will appear in the link preview when
              sharing resources (problems/contests) with others. The recommended size for the module picture is{' '}
              <b>1200x630 pixels</b>. The image will be cropped to fit this aspect ratio.
            </p>
          </div>
          <div className="col-span-4 flex flex-col gap-2 items-center w-full">
            <div className="w-full">
              <label className="mb-3 text-sm font-medium text-white" htmlFor="file_input">
                Upload File
              </label>
              <input
                id="file_input"
                className="w-full text-sm text-gray-400 border rounded-lg cursor-pointer focus:outline-none 
                  bg-gray-700 border-gray-600 placeholder-gray-400 file:px-3 file:py-2 file:mr-3 file:border-0"
                type="file"
                accept=".png, .jpg, .jpeg, .gif"
                onChange={handleImageChange}
              />
              <p className="mt-1 text-sm text-gray-300" id="file_input_help">
                Supported formats: PNG, JPG. Max dimensions: <b>1200x630px</b>. Max file size: <b>2.5MB</b>.
              </p>
            </div>

            <div className="w-full">
              <div className="w-full max-w-[1200px] aspect-[1.91] bg-gray-800 flex justify-center items-center mx-auto select-none">
                {fileUploading ? (
                  <div className="bg-gray-800 bg-opacity-80 w-full h-full flex items-center justify-center">
                    <CircularProgress color="success" />
                  </div>
                ) : image ? (
                  <div
                    className="relative w-full h-full"
                    onMouseEnter={() => setIsImageHovered(true)}
                    onMouseLeave={() => setIsImageHovered(false)}
                  >
                    <img src={image} alt="Uploaded module" className="w-full h-full object-cover" />
                    {isImageHovered && (
                      <div
                        onClick={() => setIsDeleteImageModalOpen(true)}
                        className="absolute inset-0 bg-black/50 flex justify-center items-center text-white text-lg cursor-pointer"
                      >
                        <div className="flex items-center flex-col gap-1">
                          <DeleteForever fontSize="large" color="warning" />
                          Click to Delete
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-gray-400 text-center">
                    <p>Preview empty</p>
                    <p>Module has no image</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {croppingImage && (
        <ImageCropModal
          image={croppingImage}
          onSave={onFinishedCropping}
          onClose={() => setCroppingImage(false)}
          saveButtonStartIcon={<FileUpload />}
          saveButtonText="Upload and Save"
          cancelButtonText="Cancel"
        />
      )}
      {isDeleteImageModalOpen && (
        <ConfirmationModal
          title="Delete Module Picture"
          message="Are you sure you want to delete the module picture?"
          onAccept={() => handleDeleteImage()}
          onClose={() => setIsDeleteImageModalOpen(false)}
          variant="delete"
        />
      )}
    </div>
  );
}

export default ManageModulePicture;
